import { Stack, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    useAddCustomArticleItems,
    useEditCustomArticleItem,
    AddCustomArticleItemsRequest,
    CustomArticleItemResponse,
    useCustomArticlesDefaults,
    useDeleteCustomArticles,
    useShowCustomArticleItems,
    EditCustomArticleItemRequest,
} from "@tm/data/basket"

import { useDeleteNote, useLoadNotes, useSaveNote } from "@tm/utils"
import { Note, NoteTypes } from "@tm/models"
import { ModuleHeader } from "./ModuleHeader"
import { OwnArticleByCategoryList, OwnArticleByCategoryListHandle } from "./ArticleList"
import { useOwnArticlesByCategoryStore } from "./store"
import { DialogCustomArticle, EditDialog } from "./EditDialog"

type OwnArticlestByCategoryProps = {
    title?: string
    productGroupId: number
    onConsumableReplace(selectedConsumable: CustomArticleItemResponse): void
    onClose?: () => void
}

export function OwnArticlestByCategory({ title, productGroupId, onConsumableReplace, onClose }: OwnArticlestByCategoryProps) {
    const { translateText } = useLocalization()
    const [saveError, setSaveError] = useState<string>()

    const { categoryId, customArticlesDefaultsError } = useCustomArticlesDefaults(productGroupId)

    const { articleToEdit, setArticleToEdit, setIsSaving, searchQuery } = useOwnArticlesByCategoryStore()
    const [editNote, setEditNote] = useState<Note>()

    const { mutateAsync: saveEditedArticle } = useEditCustomArticleItem()
    const { mutateAsync: saveNewArticle } = useAddCustomArticleItems()
    const { mutateAsync: deleteArticles } = useDeleteCustomArticles()

    const listHandle = useRef<OwnArticleByCategoryListHandle>(null)

    const { saveNote } = useSaveNote()
    const { deleteNote } = useDeleteNote()
    const { articleNotes } = useLoadNotes(articleToEdit?.type === "edit" ? [articleToEdit.id] : [])

    const listRequest = useMemo<Parameters<typeof useShowCustomArticleItems>[0]>(
        () => ({
            query: searchQuery,
            productGroupIdsFilter: productGroupId,
        }),
        [productGroupId, searchQuery]
    )
    const { data: { pages } = {}, fetchNextPage, isLoading, isFetchingNextPage } = useShowCustomArticleItems(listRequest)
    const articles = useMemo(() => pages?.flatMap((x) => x.data) ?? [], [pages])

    useEffect(() => {
        setEditNote(articleNotes[0])
    }, [articleNotes])

    const handleEditArticle = useCallback((article: CustomArticleItemResponse) => {
        const selectedVatRate = article.vatRates.find((vatRate) => vatRate.isSelected) ?? article.vatRates[0]
        const { id, description, version, articleNumber, isDefaultForCategory, purchasePrice, retailPrice } = article
        setArticleToEdit({
            id,
            description,
            version,
            articleNumber,
            categoryId: article.categoryId,
            isDefaultForCategory,
            purchasePrice,
            retailPrice,
            selectedVatRate,
            type: "edit",
        })
    }, [])

    const handleCloseDialog = useCallback(() => {
        setArticleToEdit(undefined)
        setSaveError(undefined)
    }, [])

    const handleDeleteArticles = useCallback(
        async (articlesResponse: CustomArticleItemResponse[]) => {
            await deleteArticles(articlesResponse.map((x) => x.id))
        },
        [deleteArticles]
    )

    const handleSaveArticle = useCallback(
        async (article: DialogCustomArticle, note?: string) => {
            try {
                setIsSaving(true)
                setSaveError(undefined)
                if (article.type === "edit") {
                    const request: EditCustomArticleItemRequest = {
                        customArticleItemToEdit: article,
                    }

                    const result = await saveEditedArticle(request)

                    if (result && note !== editNote?.text) {
                        if (note) {
                            const noteRequest: Note = editNote
                                ? { ...editNote, text: note }
                                : { type: NoteTypes.CUSTOM_ARTICLE, text: note, noteId: result.editedCustomArticleItem.id }
                            await saveNote(noteRequest)
                        } else if (editNote) {
                            await deleteNote(editNote.noteId)
                        }
                    }
                } else if (article.description) {
                    const { type, ...customArticleItemToAdd } = article
                    const request: AddCustomArticleItemsRequest = {
                        customArticleItemsToAdd: [customArticleItemToAdd],
                    }
                    await saveNewArticle(request)
                    listHandle.current?.scrollToTop()
                }
                handleCloseDialog()
            } catch {
                setSaveError(translateText(13452))
            } finally {
                setIsSaving(false)
            }
        },
        [deleteNote, editNote, handleCloseDialog, saveEditedArticle, saveNewArticle, saveNote]
    )
    return (
        <Stack gap={2} flex={1} height="100%">
            <ModuleHeader title={title} categoryId={categoryId} onClose={onClose} />
            {customArticlesDefaultsError && <Typography>{translateText(12775)}</Typography>}
            {categoryId && (
                <OwnArticleByCategoryList
                    articles={articles}
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading || isFetchingNextPage}
                    pageIndex={pages?.length ?? 0}
                    onConsumableReplace={onConsumableReplace}
                    onDeleteArticles={handleDeleteArticles}
                    onEditArticle={handleEditArticle}
                    ref={listHandle}
                />
            )}
            {articleToEdit && categoryId && (
                <EditDialog
                    onClose={handleCloseDialog}
                    onSave={handleSaveArticle}
                    article={articleToEdit!}
                    note={editNote?.text}
                    categoryId={categoryId}
                    saveError={saveError}
                />
            )}
        </Stack>
    )
}
